import React, { useState, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import { CartContext } from "components/cart";
import classNames from "lib/classNames";
import Api from "../Api";
import axios from "axios";
import { API_GATEWAY_NEW } from "aws-exports";

const Product = ({ product, add_to_unlock }) => {
  const {
    preview_product,
    Utils,
    isPreview,
    cart_settings: {
      money_format,
      rtl,
      theme: { primary, background, secondary },
      product: { sold_out }
    },
    shop_url,
    add_to_cart,
    update_cart_item,
    productNotAddedToCart,
    setVariantPickerOptions,
    setVariantsPickerShow,
    addFBTUpsell,
    cart_currency,
    rechargeSettings,
    hasWriteDiscountsScope,
    setCompareAtMap
  } = useContext(CartContext);
  const subscriptionObject = rechargeSettings?.products?.find((p) => {
    return Object.keys(p)[0] === product.id.toString();
  });
  const subscription = subscriptionObject && subscriptionObject[product.id];
  const HAS_SUBSCRIPTION =
    subscription &&
    !!subscription?.selling_plan_groups?.length &&
    rechargeSettings?.widget_settings?.published;
  const { variants } = product;
  const [loading, setLoading] = useState(!product.fetched);
  const [sodOut, setSodOut] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const addToCartInit = async () => {
    if (product.isExample || !product.fetched) return;
    try {
      setLoading(true);
      const { variants } = product;
      if (Utils.productHasOneVariant({ variants }) && !HAS_SUBSCRIPTION) {
        if (Utils.variantIsOnStock(variants[0])) {
          addToCart(variants[0]);
        } else {
          setSodOut(true);
        }
        setLoading(false);
      } else {
        setLoading(false);
        setVariantPickerOptions({
          product,
          buttonText: add_to_unlock.upsell_confirm_button.text,
          onConfirm: (variant, sellingPlanId) => {
            addToCart(variant, sellingPlanId);
          }
        });
        setVariantsPickerShow(true);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(!product.fetched)
    if (product?.variants?.length) {
      setCompareAtMap(product.variants?.reduce((acc, variant) => {
        if (variant.compareAtPrice) {
          acc[variant.id] = Number(variant.compareAtPrice) * 100
        }
        return acc
      }, {})
      )
    }
  }, [product])

  const addToCart = (variant, sellingPlanId) => {
    const variant_id = parseInt(variant.id);
    if (productNotAddedToCart(product)) {
      add_to_cart(
        {
          variant_id,
          quantity: 1,
          selling_plan: sellingPlanId,
          product_handle: product.handle //required for preview only
        },
        (item) => {
          addFBTUpsell(item.id);
          setVariantsPickerShow(false);
        }
      );
    } else {
      update_cart_item(
        { variant_id, selling_plan: sellingPlanId, quantity: "+1" },
        (item) => {
          addFBTUpsell(item.id);
          setVariantsPickerShow(false);
        }
      );
    }
  };

  const price = Utils.formatMoney(
    parseFloat(product.variants[0].price) * 100 * parseFloat(window.Shopify.currency.rate),
    money_format
  );
  const formatText = (text) =>
    text
      .replace(/{{product_title}}/g, product.title)
      .replace(/{{product_price}}/g, price);

  return (
    <div
      className={classNames("border-b last:border-b-0 mx-2 py-2")}
      style={{
        borderColor: Utils.formatColor(add_to_unlock.borderColor)
      }}
    >
      <div className={classNames("flex")}>
        {(product.image || product.variants[0].image) && (
          <div
            className={classNames("rounded bg-cover bg-center block")}
            style={{
              backgroundImage: `url(${product.image || product.variants[0].image
                })`,
              width: 70,
              height: 70
            }}
          />
        )}
        {!product.image && !product.variants[0].image && (
          <div
            style={{
              width: 70,
              height: 70,
              fill: Utils.formatColor(secondary),
              borderColor: Utils.formatColor(secondary)
            }}
            className={classNames(
              "text-transparent flex-center rounded border"
            )}
          >
            <svg viewBox="0 0 20 20" className={classNames("w-1/2 h-1/2")}>
              <path fill="currentColor" d="M14 9l-5 5-3-2-5 3v4h18v-6z"></path>
              <path d="M19 0H1a1 1 0 0 0-1 1v18a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1V1a1 1 0 0 0-1-1zM8 6c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm0 4c1.654 0 3-1.346 3-3S9.654 4 8 4 5 5.346 5 7s1.346 3 3 3zm-6 8v-2.434l3.972-2.383 2.473 1.65c.398.264.925.21 1.262-.126l4.367-4.367L18 13.48V18H2zM18 2v8.92l-3.375-2.7a.999.999 0 0 0-1.332.073l-4.42 4.42-2.318-1.545a1 1 0 0 0-1.07-.025L2 13.233V2h16z"></path>
            </svg>
          </div>
        )}
        <div
          className={classNames("flex-1 flex flex-col", rtl ? "pr-2" : "pl-2")}
        >
          <b
            className={classNames("mb-2 overflow-hidden w-full line-clamp")}
            style={{
              fontSize: Utils.em(add_to_unlock.product_title.font_size),
              fontWeight: add_to_unlock.product_title.font_weight,
              color: Utils.formatColor(add_to_unlock.product_title.color)
            }}
          >
            {isPreview ? (
              <span
                dangerouslySetInnerHTML={{
                  __html: formatText(add_to_unlock.product_title.text)
                }}
              />
            ) : (
              <a
                href={`${window.origin}/products/${product.handle}`}
                dangerouslySetInnerHTML={{
                  __html: formatText(add_to_unlock.product_title.text)
                }}
              />
            )}
          </b>
          <div className={classNames("flex items-center flex-wrap")}>
            <div
              className={classNames(
                "flex flex-col flex-1 items-start",
                rtl ? "ml-2" : "mr-2"
              )}
            >
              <b
                className={classNames("mb-1")}
                style={{
                  fontSize: Utils.em(add_to_unlock.product_price.font_size),
                  fontWeight: add_to_unlock.product_price.font_weight,
                  color: Utils.formatColor(add_to_unlock.product_price.color)
                }}
                dangerouslySetInnerHTML={{
                  __html: formatText(add_to_unlock.product_price.text)
                }}
              />

              <button
                className={classNames("btn-link")}
                style={{
                  color: Utils.formatColor(
                    add_to_unlock.show_product_details.color
                  ),
                  fontWeight: add_to_unlock.show_product_details.font_weight,
                  fontSize: Utils.em(
                    add_to_unlock.show_product_details.font_size
                  )
                }}
                onClick={() => {
                  if (!product.isExample) preview_product(product);
                }}
              >
                {add_to_unlock.show_product_details.text}
              </button>
            </div>

            <button
              className={classNames(
                "border-solid border-2 bg-transparent btn-small py-2 relative"
              )}
              style={{
                borderColor: Utils.formatColor(
                  add_to_unlock.upsell_add_button.color || primary
                ),
                color: Utils.formatColor(
                  add_to_unlock.upsell_add_button.color || primary
                ),
                fontWeight: add_to_unlock.upsell_add_button.font_weight,
                fontSize: Utils.em(add_to_unlock.upsell_add_button.font_size)
              }}
              onClick={() => addToCartInit()}
              disabled={sodOut || !product.fetched}
            >
              <span
                className={classNames({
                  "opacity-0": loading
                })}
              >
                {sodOut ? sold_out : add_to_unlock.upsell_add_button.text}
              </span>
              <div
                className={classNames("absolute inset-0 flex-center", {
                  hidden: !loading
                })}
              >
                <svg
                  aria-hidden="true"
                  focusable="false"
                  data-icon="spinner-third"
                  role="img"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                  className={classNames("w-3 h-3 fa-spin")}
                >
                  <path
                    fill="currentColor"
                    d="M456.433 371.72l-27.79-16.045c-7.192-4.152-10.052-13.136-6.487-20.636 25.82-54.328 23.566-118.602-6.768-171.03-30.265-52.529-84.802-86.621-144.76-91.424C262.35 71.922 256 64.953 256 56.649V24.56c0-9.31 7.916-16.609 17.204-15.96 81.795 5.717 156.412 51.902 197.611 123.408 41.301 71.385 43.99 159.096 8.042 232.792-4.082 8.369-14.361 11.575-22.424 6.92z"
                  ></path>
                </svg>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

Product.propTypes = {};

export default Product;
